import { useEffect, useMemo, useState } from 'react';
import InternalBarcodePreview from './InternalBarcodePreview';
import { downloadInternalBarcodeLabelsPdf } from '../../utils/internalBarcodeLabelPdf';
import {
  buildInternalBarcodeLabelCandidates,
  candidatesToPrintLabels,
  defaultLabelSelectionByKey,
  formatRetailPriceLabel,
} from '../../utils/retailBarcodeLabelSpecs';

export default function ProduitIdentificationBarcodes({
  produitId,
  produitNom,
  prix,
  prixPromo,
  codeBarres,
  referenceInterne,
  onReferenceInterneChange,
  internalBarcode,
  onInternalBarcodeChange,
  codeBarresError,
  onCodeBarresChange,
  onGenerateInternal,
  productOptions = [],
  choixInternalById = {},
  onGenerateChoixInternal,
  disabled = false,
}) {
  const [busy, setBusy] = useState(false);
  const [choixBusyId, setChoixBusyId] = useState(null);
  const [printFormat, setPrintFormat] = useState('a4');
  const [labelCopies, setLabelCopies] = useState(1);
  const [printError, setPrintError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [selectionByKey, setSelectionByKey] = useState({});

  const choixRows = useMemo(
    () => (productOptions || []).flatMap((opt) =>
      (opt.choix || []).map((choix) => ({
        id: choix.id,
        nom: choix.nom,
        prix: choix.prix,
        internalBarcode: choixInternalById[choix.id] || choix.internalBarcode || null,
      })),
    ),
    [productOptions, choixInternalById],
  );

  const labelCandidates = useMemo(
    () => buildInternalBarcodeLabelCandidates({
      produitNom,
      prix,
      prixPromo,
      internalBarcode,
      productOptions,
      choixInternalById,
    }),
    [produitNom, prix, prixPromo, internalBarcode, productOptions, choixInternalById],
  );

  const candidateSignature = useMemo(
    () => labelCandidates.map((c) => `${c.key}:${c.code}:${c.defaultSelected}`).join('|'),
    [labelCandidates],
  );

  useEffect(() => {
    setSelectionByKey(defaultLabelSelectionByKey(labelCandidates));
  }, [candidateSignature, labelCandidates]);

  const handleGenerate = async () => {
    if (!produitId || internalBarcode?.code) return;
    setBusy(true);
    try {
      await onGenerateInternal?.();
    } finally {
      setBusy(false);
    }
  };

  const handleGenerateChoix = async (choixId) => {
    setChoixBusyId(choixId);
    try {
      await onGenerateChoixInternal?.(choixId);
    } finally {
      setChoixBusyId(null);
    }
  };

  const collectLabelSpecs = () => candidatesToPrintLabels(labelCandidates, selectionByKey);

  const downloadLabelsPdf = async () => {
    const labels = collectLabelSpecs();
    if (!labels.length) return;
    setPrintError('');
    setPdfBusy(true);
    try {
      const result = await downloadInternalBarcodeLabelsPdf({
        labels,
        format: printFormat,
        copiesPerLabel: labelCopies,
      });
      if (!result.ok) {
        setPrintError(result.error || 'Impossible de générer le PDF.');
      }
    } finally {
      setPdfBusy(false);
    }
  };

  const hasAnyBarcode = labelCandidates.length > 0;
  const selectedCount = labelCandidates.filter((c) => selectionByKey[c.key]).length;

  return (
    <div
      data-produit-section="identification-codes-barres"
      className="md:col-span-2 space-y-4 rounded-lg border border-gray-200 p-4 bg-gray-50/80"
    >
      <h3 className="text-sm font-semibold text-gray-900">Identification et codes-barres</h3>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Code-barres fabricant (EAN / GTIN)</label>
        <input
          type="text"
          inputMode="numeric"
          data-testid="produit-code-barres"
          value={codeBarres}
          disabled={disabled}
          onChange={(e) => onCodeBarresChange?.(e.target.value)}
          aria-invalid={codeBarresError ? 'true' : undefined}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 ${
            codeBarresError ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Optionnel — code du fabricant"
        />
        {codeBarresError ? (
          <p className="text-xs text-red-600 mt-1" data-testid="produit-code-barres-error">
            {codeBarresError}
          </p>
        ) : (
          <p className="text-xs text-gray-500 mt-1">EAN-8, EAN-13 ou GTIN-14. Distinct du code interne Clickorico.</p>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 mb-1">Référence interne (SKU)</label>
        <input
          type="text"
          data-testid="produit-reference-interne"
          value={referenceInterne}
          disabled={disabled}
          onChange={(e) => onReferenceInterneChange?.(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg"
          placeholder="Optionnel — repère métier, non scanné"
        />
      </div>

      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-900">Code-barres interne Clickorico</label>
        {internalBarcode?.code ? (
          <InternalBarcodePreview code={internalBarcode.code} className="bg-white p-3 rounded border" />
        ) : (
          <p className="text-sm text-gray-600">Aucun code interne — générez-en un pour étiqueter et scanner en caisse.</p>
        )}
        {labelCandidates.length > 0 && (
          <fieldset className="space-y-2 rounded border border-gray-200 bg-white p-3">
            <legend className="text-sm font-medium text-gray-800 px-1">Étiquettes à imprimer</legend>
            <p className="text-xs text-gray-500">
              Prix de vente caisse (promo + supplément variante). Cochez le produit parent si vous devez étiqueter sa référence.
            </p>
            <ul className="space-y-1.5">
              {labelCandidates.map((c) => {
                const priceHint = formatRetailPriceLabel(c.unitPrice);
                const labelDesc = c.variantLabel
                  ? `${c.title} — ${c.variantLabel}`
                  : c.title;
                return (
                  <li key={c.key} className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      id={`label-select-${c.key}`}
                      data-testid={`produit-label-select-${c.key}`}
                      checked={Boolean(selectionByKey[c.key])}
                      disabled={disabled}
                      onChange={(e) => {
                        setSelectionByKey((prev) => ({
                          ...prev,
                          [c.key]: e.target.checked,
                        }));
                      }}
                      className="mt-1"
                    />
                    <label htmlFor={`label-select-${c.key}`} className="flex-1 cursor-pointer">
                      <span className="font-medium text-gray-900">{labelDesc}</span>
                      <span className="block font-mono text-xs text-gray-600">{c.code}</span>
                      <span className="block text-xs text-gray-500">
                        {priceHint ?? 'Prix selon autres options obligatoires'}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        )}
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            data-testid="produit-generate-internal-barcode"
            disabled={disabled || !produitId || busy || Boolean(internalBarcode?.code)}
            onClick={handleGenerate}
            className="px-3 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white disabled:opacity-50"
          >
            {internalBarcode?.code ? 'Code déjà attribué' : busy ? 'Génération…' : 'Générer un code-barres'}
          </button>
          <select
            value={printFormat}
            onChange={(e) => setPrintFormat(e.target.value)}
            className="text-sm border rounded-lg px-2"
            aria-label="Format PDF étiquettes"
          >
            <option value="a4">A4 — planche autocollante</option>
            <option value="thermal">Thermique 80 × 40 mm</option>
          </select>
          <label className="flex items-center gap-1 text-sm text-gray-700">
            <span className="sr-only">Nombre d&apos;exemplaires par code</span>
            <input
              type="number"
              min={1}
              max={99}
              value={labelCopies}
              onChange={(e) => setLabelCopies(Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)))}
              className="w-14 border rounded-lg px-2 py-2 text-sm"
              aria-label="Exemplaires par étiquette"
            />
            <span>ex.</span>
          </label>
          <button
            type="button"
            data-testid="produit-download-internal-labels-pdf"
            disabled={disabled || pdfBusy || !hasAnyBarcode || selectedCount === 0}
            onClick={downloadLabelsPdf}
            className="px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white"
          >
            {pdfBusy ? 'PDF…' : 'Télécharger les étiquettes PDF'}
          </button>
        </div>
        {printError ? (
          <p className="text-xs text-red-600 mt-1" data-testid="produit-print-internal-labels-error" role="alert">
            {printError}
          </p>
        ) : null}
      </div>

      {choixRows.length > 0 && (
        <div className="space-y-3 pt-2 border-t border-gray-200">
          <p className="text-sm font-medium text-gray-800">Variantes (options)</p>
          {choixRows.map((row) => (
            <div key={row.id} className="flex flex-col sm:flex-row sm:items-center gap-2 bg-white p-2 rounded border">
              <span className="text-sm flex-1">{row.nom}</span>
              {row.internalBarcode?.code ? (
                <span className="font-mono text-xs">{row.internalBarcode.code}</span>
              ) : (
                <button
                  type="button"
                  data-testid={`choix-generate-internal-barcode-${row.id}`}
                  disabled={disabled || !row.id || choixBusyId === row.id}
                  onClick={() => handleGenerateChoix(row.id)}
                  className="text-sm px-2 py-1 rounded border"
                >
                  Générer variante
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
